<template>
  <div class="statistics-container" v-loading="loading">
    <el-row :gutter="24">
      <el-col :span="8">
        <div class="stat-card members-card">
          <div class="stat-header">
            <div class="stat-icon">
              <i class="el-icon-user"></i>
            </div>
            <div class="stat-title">Club Members</div>
          </div>
          <div class="stat-main">
            <div class="stat-number">{{ memberStats.total }}</div>
            <div class="stat-label">Total Members</div>
          </div>
          <div class="stat-breakdown">
            <div class="stat-item">
              <div class="item-value">{{ memberStats.leaders }}</div>
              <div class="item-label">Leaders</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="item-value">{{ memberStats.members }}</div>
              <div class="item-label">Members</div>
            </div>
          </div>
        </div>
      </el-col>

      <el-col :span="8">
        <div class="stat-card activities-card">
          <div class="stat-header">
            <div class="stat-icon">
              <i class="el-icon-data-line"></i>
            </div>
            <div class="stat-title">Activities</div>
          </div>
          <div class="stat-main">
            <div class="stat-number">{{ activityStats.total }}</div>
            <div class="stat-label">Total Activities</div>
          </div>
          <div class="stat-breakdown">
            <div class="stat-item">
              <div class="item-value">{{ activityStats.events }}</div>
              <div class="item-label">Events</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="item-value">{{ activityStats.posts }}</div>
              <div class="item-label">Posts</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="item-value">{{ activityStats.todos }}</div>
              <div class="item-label">Todos</div>
            </div>
          </div>
        </div>
      </el-col>

      <el-col :span="8">
        <div class="stat-card recognition-card">
          <div class="stat-header">
            <div class="stat-icon">
              <i class="el-icon-trophy"></i>
            </div>
            <div class="stat-title">Recognition</div>
          </div>
          <div class="stat-main">
            <div class="stat-number">{{ achievementStats.achievements + achievementStats.honors }}</div>
            <div class="stat-label">Total Recognition</div>
          </div>
          <div class="stat-breakdown">
            <div class="stat-item">
              <div class="item-value">{{ achievementStats.achievements }}</div>
              <div class="item-label">Internal</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="item-value">{{ achievementStats.honors }}</div>
              <div class="item-label">External</div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'

export default {
  name: 'Statistics',
  props: {
    clubInfo: {
      type: Object,
      required: true
    },
    club_id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      memberStats: {
        total: 0,
        leaders: 0,
        members: 0
      },
      activityStats: {
        total: 0,
        events: 0,
        posts: 0,
        todos: 0
      },
      achievementStats: {
        achievements: 0,
        honors: 0
      }
    }
  },
  mounted() {
    this.fetchStatistics()
  },
  methods: {
    async fetchStatistics() {
      try {
        this.loading = true
        const response = await postRequest('getClubStatistics', {
          club_id: this.club_id
        })

        if (response.data.code === 0) {
          const stats = response.data.data
          this.memberStats = stats.memberStats
          this.activityStats = stats.activityStats
          this.achievementStats = stats.achievementStats
        } else {
          this.$message.error('Failed to fetch statistics')
        }
      } catch (error) {
        console.error('Error fetching statistics:', error)
        this.$message.error('Failed to fetch statistics')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.statistics-container {
  padding: 30px 20px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  .stat-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      font-size: 24px;
      color: white;
    }

    .stat-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }

  .stat-main {
    margin-bottom: 24px;
    text-align: center;

    .stat-number {
      font-size: 48px;
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 8px;
    }

    .stat-label {
      font-size: 13px;
      color: #909399;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 500;
    }
  }

  .stat-breakdown {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-top: 20px;
    border-top: 2px solid #f5f7fa;

    .stat-item {
      flex: 1;
      text-align: center;

      .item-value {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 6px;
        color: #606266;
      }

      .item-label {
        font-size: 12px;
        color: #909399;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
    }

    .stat-divider {
      width: 1px;
      height: 40px;
      background: linear-gradient(to bottom, transparent, #e4e7ed, transparent);
    }
  }

  &.members-card {
    .stat-icon {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .stat-number {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  &.activities-card {
    .stat-icon {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    }

    .stat-number {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  &.recognition-card {
    .stat-icon {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }

    .stat-number {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
}
</style> 